import { s, typographyTokens } from '@/theme/tokens';
import { Box } from '@/ui/primitives';
import { TABLE_PANEL_HEADER_HEIGHT } from './tableConstants';
import type { ReactElement } from 'react';

export type NativeTablePanelTabDefinition<T extends string> = {
  label: string;
  value: T;
};

type NativeTablePanelTabsProps<T extends string> = {
  ariaLabel: string;
  onChange: (value: T) => void;
  tabs: ReadonlyArray<NativeTablePanelTabDefinition<T>>;
  value: T;
};

export const NativeTablePanelTabs = <T extends string>({
  ariaLabel,
  onChange,
  tabs,
  value,
}: NativeTablePanelTabsProps<T>): ReactElement => (
  <Box
    role="tablist"
    aria-label={ariaLabel}
    sx={{
      alignItems: 'center',
      display: 'flex',
      gap: s('sm'),
      minHeight: TABLE_PANEL_HEADER_HEIGHT,
      pl: s('sm'),
    }}
  >
    {tabs.map((tab) => {
      const selected = tab.value === value;
      return (
        <Box
          key={tab.value}
          component="button"
          type="button"
          role="tab"
          aria-selected={selected}
          tabIndex={selected ? 0 : -1}
          onClick={() => {
            onChange(tab.value);
          }}
          sx={{
            appearance: 'none',
            background: 'transparent',
            border: 0,
            borderBottom: selected ? '1px solid currentColor' : '1px solid transparent',
            color: 'text.primary',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: typographyTokens.body.fontSize,
            fontWeight: typographyTokens.caption.fontWeight,
            lineHeight: (theme) => theme.typography.body1.lineHeight,
            minHeight: TABLE_PANEL_HEADER_HEIGHT,
            px: s('md'),
          }}
        >
          {tab.label}
        </Box>
      );
    })}
  </Box>
);
